import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { contactDetailsRequireReclassification } from "tng-address-collection-shared/contactDetailsRequireReclassification.ts";
import {
  EditCardRequest,
  EditCardViewResponse,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { queryKeys } from "./queryKeys.ts";

export const useEditCardEditCache = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (newCard: EditCardRequest) => {
      // A shared card has an edit view per project; all of them show the card.
      queryClient.setQueriesData<EditCardViewResponse>(
        { queryKey: queryKeys.cardEditViewsOfCard(newCard.id) },
        (oldCard) => {
          if (!oldCard) return oldCard;

          const contactDetailsChanged = contactDetailsRequireReclassification(
            {
              firstName: oldCard.recipient.firstName,
              lastName: oldCard.recipient.lastName,
              email: oldCard.recipient.email,
              salutation: oldCard.recipient.salutationKind,
            },
            {
              firstName: newCard.recipient.firstName,
              lastName: newCard.recipient.lastName,
              email: newCard.recipient.email,
              salutation: newCard.recipient.salutationKind,
            },
          );
          const hasContactDetailsWarning =
            !contactDetailsChanged && oldCard.hasContactDetailsWarning;
          const preservedContactDetailsWarning = hasContactDetailsWarning
            ? oldCard.contactDetailsWarning
            : undefined;

          const tempCard: EditCardViewResponse = {
            id: newCard.id,
            projectId: oldCard.projectId,
            departmentName: newCard.departmentName,
            senderId: newCard.senderId,
            manualDispatch: newCard.manualDispatch,
            comment: newCard.comment,
            receivesGift: newCard.receivesGift,
            formality: newCard.formality,
            recipient: {
              firstName: newCard.recipient.firstName,
              lastName: newCard.recipient.lastName,
              companyName: newCard.recipient.companyName,
              email: newCard.recipient.email,
              preferredLanguage: newCard.recipient.preferredLanguage,
              title: newCard.recipient.title,
              salutationKind: newCard.recipient.salutationKind,
            },
            hasContactDetailsWarning,
            isSharedByOtherProjects: oldCard.isSharedByOtherProjects,
          };

          if (preservedContactDetailsWarning !== undefined) {
            tempCard.contactDetailsWarning = preservedContactDetailsWarning;
          }

          return tempCard;
        },
      );
    },
    [queryClient],
  );
};
